'use client';

import Link from 'next/link';
import { Logger } from '@/core/utils/logger';
const logger = new Logger('api');
import { useState } from 'react';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

// Brand colors
const primaryColor = BRAND_BLUE;
const accentGreen = BRAND_GREEN;
const accentYellow = BRAND_YELLOW;

export default function ContactMobilePage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/public/external-requests/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.name,
          lastName: '',
          email: form.email,
          phone: '',
          company: '',
          message: form.message,
          reason: 'general',
        }),
      });
      if (!response.ok) throw new Error('Failed to submit');
      setSubmitted(true);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      logger.error('Failed to submit contact form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div
        className="relative overflow-hidden py-10 sm:py-12"
        style={{
          backgroundImage: `linear-gradient(135deg, ${primaryColor}ee 0%, ${accentGreen}dd 50%, ${accentYellow}cc 100%)`,
        }}
      >
        <div className="max-w-lg mx-auto px-4 text-center">
          <h1 className={`text-2xl sm:text-3xl ${poppins.className} tracking-tight font-medium text-white`}>
            Get in Touch
          </h1>
          <p className="mt-2 text-base text-white/90">
            We&apos;d love to hear from you
          </p>
          {/* Brand dots */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-lg mx-auto px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Quick Contact */}
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Reach us directly</h2>
            <div className="space-y-3">
              <a
                href="mailto:support@voley.us"
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <svg className="h-5 w-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  <p className="text-sm" style={{ color: primaryColor }}>support@voley.us</p>
                </div>
              </a>
              <a
                href="tel:+18018548750"
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentGreen}20` }}>
                  <svg className="h-5 w-5 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Phone</p>
                  <p className="text-sm text-[#5aaa7a]">(801) 854-8750</p>
                </div>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Send us a message</h3>
            {submitted ? (
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: `${accentGreen}15` }}>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-3" style={{ backgroundColor: `${accentGreen}25` }}>
                  <svg className="h-6 w-6 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900">Message sent!</p>
                <p className="text-sm text-gray-600 mt-1">We&apos;ll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow resize-none"
                    placeholder="How can we help?"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white rounded-full py-3 text-sm font-medium transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-60"
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <Link href="/home" className="text-sm font-medium hover:underline" style={{ color: primaryColor }}>
            Home
          </Link>
          <span className="text-gray-300">|</span>
          <Link href="/solutions" className="text-sm font-medium hover:underline" style={{ color: primaryColor }}>
            Solutions
          </Link>
          <span className="text-gray-300">|</span>
          <Link href="/pricing" className="text-sm font-medium hover:underline" style={{ color: primaryColor }}>
            Pricing
          </Link>
        </div>
        <p className="text-center text-xs text-gray-400 mt-4">
          For full access, open Voley on your desktop browser.
        </p>
      </div>
    </div>
  );
}
